import { AlertTriangle, X } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Saved preset thermostat IDs that the account's live Seam roster no longer
 * returns (device removed, account disconnected, access revoked). The picker
 * only renders live devices, so without this list a host could never untick
 * these IDs and every Update would silently send them back.
 *
 * Removal stays server-validated: if PadPulse still holds a provider
 * restriction on an unreachable device, the update is refused (409) so a
 * lockout is never left behind on the thermostat.
 */
export function getUnavailablePresetDeviceIds(selectedIds: string[], liveDeviceIds: Iterable<string>): string[] {
  const live = new Set(liveDeviceIds);
  return selectedIds.filter((id) => !live.has(id));
}

function shortId(id: string): string {
  return id.length > 8 ? `…${id.slice(-8)}` : id;
}

export function ThermostatPresetUnavailableDevices({
  deviceIds,
  onRemove,
}: {
  deviceIds: string[];
  onRemove: (deviceId: string) => void;
}) {
  if (deviceIds.length === 0) return null;
  return (
    <div
      className="mb-2 rounded-lg border border-amber-200 bg-amber-50 p-2 dark:border-amber-900/50 dark:bg-amber-950/30"
      role="group"
      aria-label="Unavailable thermostats"
      data-testid="preset-unavailable-devices"
    >
      <p className="flex items-center gap-1.5 px-1 text-xs font-medium text-amber-800 dark:text-amber-300">
        <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
        {deviceIds.length === 1 ? "1 saved thermostat is" : `${deviceIds.length} saved thermostats are`} no longer in your connected accounts
      </p>
      <p className="mb-1 px-1 text-[11px] text-amber-700 dark:text-amber-400">
        Remove them to stop this preset from targeting them. Saving keeps any you leave here.
      </p>
      <ul className="space-y-1">
        {deviceIds.map((id) => (
          <li
            key={id}
            className="flex items-center justify-between gap-2 rounded bg-white/70 px-2 py-1 dark:bg-black/20"
            data-testid={`preset-unavailable-device-${id}`}
          >
            <span className="min-w-0 truncate text-sm text-gray-800 dark:text-gray-200" title={id}>
              Unavailable thermostat <span className="font-mono text-xs text-gray-500">{shortId(id)}</span>
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 shrink-0 px-2 text-xs"
              onClick={() => onRemove(id)}
              aria-label={`Remove unavailable thermostat ${shortId(id)} from preset`}
              data-testid={`button-remove-unavailable-device-${id}`}
            >
              <X className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
              Remove
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}
